import { Box, Button, TextField } from "@mui/material";
import { type UseMutationResult, useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { type FC, useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthProvider";
import { useAnalytics } from "../../contexts/analytics/hooks";
import { useSnackbar } from "../../contexts/SnackbarContext";
import { OTP_CODE_LENGTH, SigninLayout } from "./common";

function useVerifySMS(username: string): UseMutationResult<void, Error, { code: string }, void> {
	const { showSnackbar } = useSnackbar();
	const { verifyCode } = useAuth();
	const { track, trackError } = useAnalytics();
	const navigate = useNavigate();

	return useMutation<void, Error, { code: string }, void>({
		mutationKey: ["useVerifySMS", username],
		mutationFn: async ({ code }) => {
			try {
				track("mfa", { status: "start" });

				const { isSuccessful } = await verifyCode(username, code);
				if (!isSuccessful) {
					trackError("mfa");
				}

				navigate("/");
			} catch (e) {
				console.error(e);

				let message = "Could not verify code";
				if (e instanceof AxiosError) {
					message = e.response?.data?.detail;
				} else if (e instanceof Error) {
					message = e.message;
				}
				trackError("mfa", { status: "fail", error: message });
				showSnackbar(message, "error");
			}
		},
	});
}

export const MultiFactorAuth: FC = () => {
	const { t } = useTranslation();
	const { showSnackbar } = useSnackbar();
	const navigate = useNavigate();
	const { state } = useLocation();
	const sub = state?.sub as string | undefined;
	const username = state?.username as string | undefined;
	const { verifyCode } = useAuth();

	useEffect(() => {
		if (!username) {
			showSnackbar(t("error.sso.login.failed"), "error");
			navigate("/signin", { replace: true });
		}
	}, [username, showSnackbar, t, navigate]);

	const { mutateAsync: verifyMFA, isPending: isVerifyMFAPending } = useVerifySMS(username ?? "");

	const submitMFACode = useCallback(
		async (formData: FormData) => {
			const code = formData.get("code")?.toString();
			if (!code?.length) {
				showSnackbar(t("please.fill.a.valid.code"), "error");
				return;
			}

			try {
				if (sub) {
					await verifyMFA({ code });
				} else {
					const { isSuccessful } = await verifyCode(username ?? "", code);
					if (isSuccessful) {
						navigate("/");
					}
				}
			} catch (e) {
				console.error(e);
				showSnackbar(t("could.not.verify.one.time.password"), "error");
			}
		},
		[showSnackbar, verifyMFA, verifyCode, sub, username, navigate, t],
	);

	const inputRef = useRef<HTMLInputElement>(null);
	useEffect(() => {
		inputRef.current?.focus();
	}, []);

	if (!username) return null;

	return (
		<SigninLayout title="Type Your Code">
			<Box component="form" sx={{ display: "contents" }} action={submitMFACode}>
				<TextField
					data-testid="mfa-code-field"
					ref={inputRef}
					type="text"
					name="code"
					variant="outlined"
					size="small"
					placeholder="Verification code"
					required
					slotProps={{
						// "one-time-code" lets the browser suggest the code from the SMS.
						// It fully auto-fills on iOS; on Android it needs a special line in the SMS that this
						// MFA flow can't add (the text is sent by Twilio's service, which we don't control).
						htmlInput: { minLength: OTP_CODE_LENGTH, maxLength: OTP_CODE_LENGTH, autoComplete: "one-time-code", inputMode: "numeric" },
					}}
				/>

				<Button data-testid="mfa-verify-button" type="submit" variant="contained" loading={isVerifyMFAPending}>
					Verify and Proceed
				</Button>
			</Box>
		</SigninLayout>
	);
};
